import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Subscription, ReplaySubject, BehaviorSubject, combineLatest, distinct } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags, ShareScreenVideoCodec } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { SnackBarComponent, SnackBarData, SnackBarType } from 'app/shared/snackbar/snackbar.component';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { filterCodecs } from 'app/utils/media/rtc';
import { cleanTracks } from 'app/utils/media/stream';
import { isUndefined } from 'app/utils/stream-util';
import User from 'entity/User';
import {
  ScreenShare,
  Presenter,
  SharingPublishCandidateMessage,
  OutgoingMessageType,
  PeerCandidate,
  StartSharingMessage,
  IncomingMessageType,
  ScreenShareStatus,
  EndSharingMessage,
  UpdateType,
  IncomingMessage,
  PresenterType,
} from 'messages/websocket.messages';

import { Player } from '../../social-pixi-canvas/player';
import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../../state/app';
import { RESOLUTION_1080P, RESOLUTION_STANDARD } from '../../types/media';
import { BrowserService } from '../browser.service';
import { HeaderToastMessageService } from '../header-toast-message.service';
import { PublisherType, RtcStatsStreamService, StatsCounter } from '../rtc-stats-stream';
import { SceneService } from '../scene.service';
import { processScreenShareError } from '../share-screen-helpers/share-screen-error.helper';
import { LoadingTransitionLabel, TransitionService } from '../transition.service';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

import { GroupShareScreenParticipantService } from './group-share-screen-participant.service';
import { GroupShareScreenRecorderService } from './group-share-screen-recorder.service';
import { PUBLISHER_SCREEN_SHARE_ENDED_MESSAGE } from './types';

@Injectable({
  providedIn: 'root',
})
export class GroupShareScreenService extends Loggable {
  private screenStream?: MediaStream;
  private user?: User;

  // WebRTC Peer connection
  private publisherCandidates: SharingPublishCandidateMessage[] = [];
  private _peer?: RTCPeerConnection;

  // Track peer connection start time (unix milliseconds)
  private peerConnectionTime?: number;

  // Group Shares
  private screenSharingUserId?: string;
  private groupPlayers: Player[] = [];
  public groupSharingInProgress = false;
  // status of a groupshare in my group, regardless of whether i have joined
  private isGroupShareActiveOrAvailable = new BehaviorSubject<boolean>(false);
  public isGroupShareActiveOrAvailable$ = this.isGroupShareActiveOrAvailable.asObservable();
  private groupScreenShareStatus = ScreenShareStatus.NONE;

  private isUserSharing = false;
  private isWideRecording = false;

  private userId?: string;
  private socialId?: string;
  private isGuest = false;
  private helpLinkScreenShareError?: string;
  private websocketSubscription?: Subscription;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private codecSetting: ShareScreenVideoCodec = { mimeType: '', sdpFormatFilter: '' };
  private group: Player[] = [];

  public settings = {
    basic: {
      frameRate: 16,
      bitrate: RESOLUTION_STANDARD.bitrate,
      hasAudio: false,
    },
    enhanced: {
      frameRate: 30,
      bitrate: RESOLUTION_1080P.bitrate,
      hasAudio: true,
    },
  };

  protected enhanced = false;
  private reqestingCandidate = false;

  public constructor(
    private dialog: MatDialog,
    private videoRoomService: VideoRoomService,
    private websocketService: WebsocketService,
    private rtcStatsStreamService: RtcStatsStreamService,
    private sceneService: SceneService,
    private headerToastMessageService: HeaderToastMessageService,
    private groupShareParticipantService: GroupShareScreenParticipantService,
    private groupScreenShareRecorderService: GroupShareScreenRecorderService,
    private store: Store,
    private browserService: BrowserService,
    private transitionService: TransitionService,
    private groupShareScreenParticipantService: GroupShareScreenParticipantService,
    private _snackBar: MatSnackBar,
  ) {
    super();
    this.setDisplayName('GroupScreenShareService');
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject<boolean>(1);

    // Hosts Can End Group Screen Shares
    // The screen sharing user id may not be the user
    this.groupShareParticipantService.groupShareId$.pipe(takeUntil(this.destroyed$)).subscribe((id) => {
      this.screenSharingUserId = id;
    });

    // User state
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ isGuest, user }) => {
        this.user = user;
        this.userId = user?.userId;
        this.isGuest = isGuest;
      });

    // Social state
    this.store
      .select(socialAppSelectors.getJoinedSocialId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialId) => (this.socialId = socialId));

    // Presentation state
    this.store
      .select(socialAppSelectors.isEventActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async (active) => {
          if (active && this.isUserSharing && this.userId) {
            await this.stopSharing(this.userId);
            this.displayBannerMessage(PUBLISHER_SCREEN_SHARE_ENDED_MESSAGE);
          }
        },
        error: (e: unknown) => this.error('isEventActive', { e }),
      });

    // Group Screen Share state
    combineLatest([
      this.sceneService.primaryGroupMembers$,
      this.store.select(socialAppSelectors.getGroupShareIds),
      this.store.select(socialAppSelectors.isUserSharingScreenToGroup),
    ])
      .pipe(distinct(), takeUntil(this.destroyed$))
      .subscribe(([group, groupShareIds, isUserSharing]) => {
        const userId = this.userId ?? '';
        this.isUserSharing = isUserSharing;
        if (isUserSharing) {
          this.screenSharingUserId = userId;
        }
        this.group = group;
        if (
          // is user presenter?
          isUserSharing ||
          // is someone in my group presenter?
          this.group.some((r) => groupShareIds.includes(r.userId))
        ) {
          this.isGroupShareActiveOrAvailable.next(true);
          this.groupScreenShareStatus = ScreenShareStatus.ACTIVE;
        } else {
          this.isGroupShareActiveOrAvailable.next(false);
          this.groupScreenShareStatus = ScreenShareStatus.NONE;
        }
      });

    // Feature Flags
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.share_screen_video_codec)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((codecSetting) => (this.codecSetting = codecSetting));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.help_link_screen_share_error)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((url) => (this.helpLinkScreenShareError = url));

    // Notification Status
    this.groupSharingInProgress = false;
    this.groupScreenShareRecorderService.setGroupSharingInProgress(false);

    this.settings.basic.bitrate = this.videoRoomService.getSvcSettings()?.bitrate || RESOLUTION_STANDARD.bitrate;

    this.store
      .select(socialAppSelectors.isWideRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isWideRecording) => {
        this.isWideRecording = isWideRecording;
      });
  }

  public async destroy(): Promise<void> {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    if (this.screenSharingUserId && this.userId === this.screenSharingUserId) {
      await this.stopSharing(this.screenSharingUserId);
    }
  }

  // Websocket Message Listener
  private wsMessageListener = async (msg: IncomingMessage): Promise<void> => {
    const { t } = msg;
    switch (t) {
      case IncomingMessageType.SHR_PUBANS: {
        if (!this.peer) {
          return;
        }
        // After publish add screenshare to state
        await this.addScreenshareToState();

        const { d } = msg;
        await this.peer.setRemoteDescription(d as RTCSessionDescriptionInit);
        return;
      }
      case IncomingMessageType.SHR_ENDED: {
        const { userId } = msg;
        if (this.userId === userId) {
          await this.stopSharing(this.userId);
        }
        return;
      }
    }
  };

  public isHost(): boolean {
    return this.screenSharingUserId === this.userId;
  }

  /**
   * Initiate screen share
   *
   * @returns Boolean - true if screen share started
   */
  public async startSharing(enhanced = false): Promise<boolean> {
    if (!this.user) {
      this.fatal('Cannot screen share without a user set');
      return false;
    }

    assertDefined(this.user.userId);

    this.info('Starting group screen share');

    this.enhanced = enhanced;

    // Init Websocket Message Listener
    this.websocketSubscription = this.websocketService.message$.subscribe(this.wsMessageListener.bind(this));

    // Request Screen Share Stream
    try {
      this.screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: enhanced
          ? {
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            }
          : true,
        audio: enhanced,
      });
    } catch (error) {
      this.showError(processScreenShareError(error));
      await this.stopSharing(this.user.userId);
      return false;
    }

    // Do not initiate if you are already watching a group screen share
    if (this.groupScreenShareStatus === ScreenShareStatus.ACTIVE) {
      this.info('Ending group screen share: share already active');
      this.screenBeingSharedWarning(
        `Unable to Screen Share, ${this.user.firstName} is sharing their screen`,
        'warning',
        'WARNING',
      );
      await this.stopSharing(this.user.userId);
      return false;
    }

    // Starts loading effect
    this.transitionService.enableTransitionOverlay(LoadingTransitionLabel.GROUPSHARE_HOST, true).catch((err) => {
      this.error('Failed to execute transition', err);
    });

    // Notify Status
    this.sceneService.enablePlayerMovement(false);
    this.groupSharingInProgress = true;
    this.groupScreenShareRecorderService.setGroupSharingInProgress(true);
    this.screenSharingUserId = this.user.userId;

    const frameRate = enhanced ? this.settings.enhanced.frameRate : this.settings.basic.frameRate;

    // Stream Configurations
    if (this.screenStream) {
      this.setFrameRate(this.screenStream, frameRate);
      this.addStreamStopListeners(this.screenStream);
      await this.createScreenSharePeer(this.screenStream);
    }

    // TODO: is this correct? throwing server error.
    if (this.isWideRecording) {
      this.groupScreenShareRecorderService.handleRecordGroupShare(true);
    }

    // Set User as joined share
    if (!this.groupShareScreenParticipantService.userHasJoinedShare) {
      this.groupShareScreenParticipantService.userHasJoinedShare = this.user.userId;
    }

    return true;
  }

  private async addScreenshareToState(): Promise<void> {
    if (!this.user) {
      this.fatal('Cannot screen share without a user set');
      return;
    }
    assertDefined(this.user.userId);
    const { firstName, lastName, userId } = this.user;
    const player = this.sceneService.getPlayer();

    const { isGuest, socialId } = this;
    const presenterType = PresenterType.SCREEN_SHARE;
    const presenter: Presenter = { userId, firstName, lastName, isGuest, socialId, presenterType };
    const screenShare: ScreenShare = {
      presenter,
      groupId: player?.groupId?.toString(),
      viewerUserIds: [],
      isGlobal: false,
    };

    await SocialStateMachine.shared.requestAddGroupScreenShare({
      screenShare,
      updateType: UpdateType.CURRENT,
      userId,
    });
  }

  private addStreamStopListeners(stream: MediaStream): void {
    const { screenSharingUserId } = this;
    const stopSharing = async () => {
      if (!screenSharingUserId) {
        return;
      }
      await this.stopSharing(screenSharingUserId);
    };
    stream.addEventListener('ended', stopSharing, false);
    stream.addEventListener('inactive', stopSharing, false);

    if (this.browserService.isSafari || this.browserService.isFirefox) {
      const videoTrack = stream.getVideoTracks()[0];
      videoTrack.addEventListener('ended', stopSharing, false);
    }
  }

  public async stopSharing(userId: string, isLeavingRoom?: boolean): Promise<void> {
    // Set State sharing screen >> FALSE
    if (this.groupSharingInProgress) {
      this.groupSharingInProgress = false;
      this.groupScreenShareRecorderService.setGroupSharingInProgress(false);

      // returns background to full opacity
      this.transitionService.enableFadeInBackground().catch((err) => {
        this.error('Failed to execute transition', err);
      });

      if (
        this.groupScreenShareRecorderService.isGroupShareRecording &&
        this.groupScreenShareRecorderService.recordingId
      ) {
        await this.groupScreenShareRecorderService.endRecording();
      }

      this.sceneService.enablePlayerMovement(true);
      assertDefined(userId);

      // TODO: this validation was added to prevent a server error
      if (isLeavingRoom) {
        SocialStateMachine.shared
          .requestRemoveGroupScreenShare({
            userId,
            updateType: UpdateType.CURRENT,
          })
          .catch((e) => this.error('Failed to end screen share', e));
      } else {
        await SocialStateMachine.shared
          .requestRemoveGroupScreenShare({
            userId,
            updateType: UpdateType.CURRENT,
          })
          .catch((e) => this.error('Failed to end screen share', e));
      }

      // TODO: this was generating a server error before status check was added, can we remove?
      if (this.socialId && this.groupScreenShareStatus === ScreenShareStatus.ACTIVE) {
        const shareEnd: EndSharingMessage = {
          t: OutgoingMessageType.SHREND,
          socialId: this.socialId,
          groupShare: true,
        };
        this.websocketService.sendMessage(shareEnd);
      }
    }

    this.websocketSubscription?.unsubscribe();
    this.websocketSubscription = undefined;

    // Destroy Transmission
    this.peer?.close();
    this.peer = undefined;

    await cleanTracks(this.screenStream, this);
  }

  private displayBannerMessage(message: string): void {
    const durationInSeconds = 5;
    this.headerToastMessageService.pushTemporaryMessage({
      message,
      durationInSeconds,
    });
  }

  /**
   * Show an error modal to the user.
   * @param text The error text to display
   */
  private showError(text: string): void {
    const helpLink = this.helpLinkScreenShareError
      ? { url: this.helpLinkScreenShareError, text: 'Understanding screen share errors' }
      : undefined;
    const message = {
      title: 'Unable to share your screen to your group',
      text: text,
      helpLink,
    };
    this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
      data: message,
    });
  }

  private setFrameRate(stream: MediaStream, frameRate: number): void {
    stream.getTracks().forEach(async (track) => {
      const constraints = track.getConstraints();
      constraints.frameRate = frameRate;
      await track.applyConstraints(constraints);
    });
  }

  // -- WebRTC Transmission --------------------------------------------------------------------------------------

  private async createScreenSharePeer(stream: MediaStream): Promise<void> {
    this.publisherCandidates = [];
    const rtcConfig = this.videoRoomService.createRtcConfig();

    // Setup WebRTC Peer and Tracks
    this.peer = new RTCPeerConnection(rtcConfig);
    this.peerConnectionTime = Date.now();

    this.peer.onicecandidate = (event) => this.publishScreenShareCandidate(event);
    this.peer.onicecandidateerror = (e) => this.trace('Candidate error', e);
    this.peer.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = this.peer ?? {};
      this.info('Publish ICE state change', { iceConnectionState, peerTime: this.peerConnectionTime });
    };

    this.peer.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = this.peer ?? {};
      this.info('Publish ICE gathering state change', { iceGatheringState, peerTime: this.peerConnectionTime });
    };

    this.peer.onconnectionstatechange = () => {
      // Peer may have already been destroyed. There's nothing to do in this case.
      if (!this.peer) {
        return;
      }
      const { connectionState } = this.peer;
      this.info('Publish state change', { connectionState });
      switch (connectionState) {
        case 'connected':
          break;

        case 'failed':
          this.handleConnectionFailed();
          break;

        default:
          break;
      }
    };

    const videoTracks = stream.getVideoTracks();
    const audioTracks = stream.getAudioTracks();
    const svc = this.videoRoomService.getSvcSettings();

    let codecs: RTCRtpCodecCapability[] | undefined;
    codecs = RTCRtpReceiver.getCapabilities('video')?.codecs;

    // TODO: Deprecated flag?
    if (this.codecSetting.mimeType && !svc) {
      const { sdpFormatFilter } = this.codecSetting;
      const formatFilter = sdpFormatFilter !== '' ? sdpFormatFilter : undefined;
      codecs = isUndefined(codecs) ? undefined : filterCodecs(codecs, this.codecSetting.mimeType, formatFilter);
    }

    const sendEncodings: RTCRtpEncodingParameters[] | undefined = svc
      ? [{ scalabilityMode: svc.shareScalabilityMode } as RTCRtpEncodingParameters]
      : undefined;

    this.info('Publishing video', { codecs, sendEncodings });

    const transceiver = this.peer?.addTransceiver(videoTracks[0], {
      direction: 'sendonly',
      sendEncodings,
    });

    // add audio tracks
    if (audioTracks.length > 0) {
      this.peer?.addTransceiver(audioTracks[0], {
        direction: 'sendonly',
      });
      for (const track of audioTracks) {
        if (!this.peer?.getSenders().find((sender) => sender.track === track)) {
          this.peer?.addTrack(track, stream);
        }
      }
    }
    // setCodecPreferences is not compatible with Firefox
    // https://caniuse.com/?search=setCodecPreferences
    if (codecs && transceiver && !this.browserService.isFirefox) {
      transceiver.setCodecPreferences(codecs);
    }

    const offer = await this.peer.createOffer();
    await this.peer.setLocalDescription(offer);

    const { sdp, type } = offer;
    if (!type || !sdp) {
      this.error('Got unexpected undefined offer value', offer);
      return;
    }

    // Send Offer
    const msg: StartSharingMessage = {
      t: OutgoingMessageType.SHR,
      offer: { type, sdp },
      groupShare: true,
      bitrate: this.enhanced ? this.settings.enhanced.bitrate : this.settings.basic.bitrate,
    };

    this.websocketService.sendMessage(msg);
    this.publisherCandidates.forEach((c) => this.websocketService.sendMessage(c));
  }

  private publishScreenShareCandidate(event: RTCPeerConnectionIceEvent): void {
    if (this.reqestingCandidate) {
      return;
    }
    // Prevent sending multiple candidates
    this.reqestingCandidate = true;
    setTimeout(() => {
      this.reqestingCandidate = false;
    }, 3000);
    const { candidate } = event;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }

    assertDefined(this.user?.userId);
    const { userId } = this.user;

    this.info('ICE Candidate received', candidate.toJSON());

    const msg: SharingPublishCandidateMessage = {
      t: OutgoingMessageType.SHRCAN,
      userId,
      d: candidate as PeerCandidate,
      groupShare: true,
    };
    if (this.websocketService.isConnected) {
      this.websocketService.sendMessage(msg);
    } else {
      this.publisherCandidates.push(msg);
    }
  }

  private handleConnectionFailed(): void {
    this.rtcStatsStreamService.incrementCounter(StatsCounter.SCREENSHARE_PUBLISHER_CONNECTION_FAILED);
    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.peerConnectionTime;

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.videoRoomService.advanceReconnectionState({ connectionTime });
    }
  }

  public get peer(): RTCPeerConnection | undefined {
    return this._peer;
  }

  private set peer(newPeer: RTCPeerConnection | undefined) {
    if (newPeer) {
      this.rtcStatsStreamService.addPublishMonitoring(PublisherType.SCREEN_SHARE, newPeer);
    } else {
      this.rtcStatsStreamService.removePublishMonitoring(PublisherType.SCREEN_SHARE);
    }
    this._peer = newPeer;
  }

  private screenBeingSharedWarning(message: string, type?: SnackBarType, icon?: string, duration = 12000) {
    const _data: SnackBarData = {
      message: message,
      type: type || '',
      icon: icon || '',
      mode: 'dark',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }
}
